
import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, Button, Badge } from '../components/ui';

const Resources = () => {
  const [activeTab, setActiveTab] = useState<'templates' | 'wellbeing' | 'links'>('templates');
  const [breathingActive, setBreathingActive] = useState(false);
  const [breathText, setBreathText] = useState('Inhale');
  
  // Breathing Animation Logic
  useEffect(() => {
      let interval: any;
      if (breathingActive) {
          let phase = 0; // 0=In, 1=Hold, 2=Out, 3=Hold
          const runPhase = () => {
              switch(phase) {
                  case 0: setBreathText('Inhale (4s)'); break;
                  case 1: setBreathText('Hold (4s)'); break;
                  case 2: setBreathText('Exhale (4s)'); break;
                  case 3: setBreathText('Hold (4s)'); break;
              }
              phase = (phase + 1) % 4;
          };
          runPhase();
          interval = setInterval(runPhase, 4000);
      } else {
          setBreathText('Ready');
      }
      return () => clearInterval(interval);
  }, [breathingActive]);

  const handleDownload = (filename: string) => {
      // Create a temporary link element to trigger the download
      const link = document.createElement('a');
      link.href = `/${filename}`; // Files in 'public' are served at root
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  };

  return (
    <Layout>
      <div className="mb-8 animate-slide-up">
        <h2 className="text-3xl font-heading font-bold text-slate-900 mb-2">Resource Library</h2>
        <p className="text-slate-500 text-lg">Templates, wellbeing tools, and useful links for your journey.</p>
        
        <div className="flex gap-2 mt-6 overflow-x-auto pb-2">
            <button 
                onClick={() => setActiveTab('templates')} 
                className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all whitespace-nowrap ${activeTab === 'templates' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/30' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
            >
                📄 Legal Templates
            </button>
            <button 
                onClick={() => setActiveTab('wellbeing')} 
                className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all whitespace-nowrap ${activeTab === 'wellbeing' ? 'bg-teal-600 text-white shadow-lg shadow-teal-500/30' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
            >
                🧘 Wellbeing & Calm
            </button>
            <button 
                onClick={() => setActiveTab('links')} 
                className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all whitespace-nowrap ${activeTab === 'links' ? 'bg-slate-700 text-white shadow-lg shadow-slate-500/30' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
            >
                🔗 External Links
            </button>
        </div>
      </div>

      {activeTab === 'templates' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in">
              <Card className="p-6 border-l-4 border-indigo-500">
                  <div className="h-12 w-12 bg-indigo-50 rounded-lg flex items-center justify-center text-2xl mb-4">📝</div>
                  <h3 className="font-bold text-lg text-slate-900">Position Statement</h3>
                  <p className="text-sm text-slate-500 mt-2 mb-4 h-10">A clean, formatted Word template for your Pre-Hearing statement.</p>
                  <Button variant="outline" onClick={() => handleDownload('Position_Statement_Template.docx')} className="w-full text-xs">Download .DOCX</Button>
              </Card>

              <Card className="p-6 border-l-4 border-indigo-500">
                  <div className="h-12 w-12 bg-indigo-50 rounded-lg flex items-center justify-center text-2xl mb-4">✍️</div>
                  <h3 className="font-bold text-lg text-slate-900">Witness Statement</h3>
                  <p className="text-sm text-slate-500 mt-2 mb-4 h-10">Includes standard headers, Exhibit cover sheets, and Statement of Truth.</p>
                  <Button variant="outline" onClick={() => handleDownload('Witness_Statement_Template.docx')} className="w-full text-xs">Download .DOCX</Button>
              </Card>

              <Card className="p-6 border-l-4 border-rose-500">
                  <div className="h-12 w-12 bg-rose-50 rounded-lg flex items-center justify-center text-2xl mb-4">🔍</div>
                  <h3 className="font-bold text-lg text-slate-900">Scott Schedule</h3>
                  <p className="text-sm text-slate-500 mt-2 mb-4 h-10">Word table template for responding to allegations clearly.</p>
                  <Button variant="outline" onClick={() => handleDownload('Scott_Schedule_Template.docx')} className="w-full text-xs">Download .DOCX</Button>
              </Card>

              <Card className="p-6 border-l-4 border-slate-600">
                  <div className="h-12 w-12 bg-slate-100 rounded-lg flex items-center justify-center text-2xl mb-4">🕵️‍♂️</div>
                  <h3 className="font-bold text-lg text-slate-900">Consistency Audit Log</h3>
                  <p className="text-sm text-slate-500 mt-2 mb-4 h-10">Cross-reference discrepancies across various witness statements and police logs.</p>
                  <Button variant="outline" onClick={() => handleDownload('Consistency_Audit_Log.docx')} className="w-full text-xs">Download .DOCX</Button>
              </Card>

              <Card className="p-6 border-l-4 border-emerald-500">
                  <div className="h-12 w-12 bg-emerald-50 rounded-lg flex items-center justify-center text-2xl mb-4">🤝</div>
                  <h3 className="font-bold text-lg text-slate-900">Parenting Plan</h3>
                  <p className="text-sm text-slate-500 mt-2 mb-4 h-10">A draft proposal for holidays, handovers, and communication rules.</p>
                  <Button variant="outline" onClick={() => handleDownload('Parenting_Plan_Draft.docx')} className="w-full text-xs">Download .DOCX</Button>
              </Card>

              <Card className="p-6 border-l-4 border-slate-500">
                  <div className="h-12 w-12 bg-slate-100 rounded-lg flex items-center justify-center text-2xl mb-4">📅</div>
                  <h3 className="font-bold text-lg text-slate-900">Contact Log</h3>
                  <p className="text-sm text-slate-500 mt-2 mb-4 h-10">Simple tracker for recording dates, times, and handover notes.</p>
                  <Button variant="outline" onClick={() => handleDownload('Contact_Log.docx')} className="w-full text-xs">Download .DOCX</Button>
              </Card>

              <Card className="p-6 border-l-4 border-amber-500">
                  <div className="h-12 w-12 bg-amber-50 rounded-lg flex items-center justify-center text-2xl mb-4">👤</div>
                  <h3 className="font-bold text-lg text-slate-900">All About Me Profile</h3>
                  <p className="text-sm text-slate-500 mt-2 mb-4 h-10">Profile template to document important personal details and preferences.</p>
                  <Button variant="outline" onClick={() => handleDownload('All About Me Profile.docx')} className="w-full text-xs">Download .DOCX</Button>
              </Card>

              <Card className="p-6 border-l-4 border-blue-500">
                  <div className="h-12 w-12 bg-blue-50 rounded-lg flex items-center justify-center text-2xl mb-4">🏥</div>
                  <h3 className="font-bold text-lg text-slate-900">School & Medical Log</h3>
                  <p className="text-sm text-slate-500 mt-2 mb-4 h-10">Keep track of important school meetings and medical appointments.</p>
                  <Button variant="outline" onClick={() => handleDownload('School & Medical Engagement Log.docx')} className="w-full text-xs">Download .DOCX</Button>
              </Card>
          </div>
      )}

      {activeTab === 'wellbeing' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-fade-in">
              {/* Breathing Tool */}
              <div className="lg:col-span-2">
                  <Card className="p-8 h-full bg-gradient-to-br from-teal-50 to-emerald-50 border-teal-200 flex flex-col items-center justify-center text-center">
                      <h3 className="text-2xl font-heading font-bold text-teal-900 mb-2">Box Breathing Tool</h3>
                      <p className="text-teal-700 mb-8 max-w-md">Feeling panicked before court? Use this 4-second box breathing technique to lower your heart rate immediately.</p>
                      
                      <div className="relative w-64 h-64 flex items-center justify-center mb-8">
                          {/* Animated Circles */}
                          <div className={`absolute inset-0 bg-teal-200 rounded-full opacity-20 transition-transform duration-[4000ms] ease-in-out ${breathingActive && (breathText.includes('Inhale') || breathText.includes('Hold')) ? 'scale-100' : 'scale-50'}`}></div>
                          <div className={`absolute inset-4 bg-teal-300 rounded-full opacity-30 transition-transform duration-[4000ms] ease-in-out ${breathingActive && (breathText.includes('Inhale') || breathText.includes('Hold')) ? 'scale-100' : 'scale-50'}`}></div>
                          <div className={`absolute inset-8 bg-teal-500 rounded-full opacity-10 transition-transform duration-[4000ms] ease-in-out ${breathingActive && (breathText.includes('Inhale') || breathText.includes('Hold')) ? 'scale-100' : 'scale-50'}`}></div>
                          
                          <div className="relative z-10">
                              <p className="text-3xl font-bold text-teal-800 transition-all">{breathText}</p>
                          </div>
                      </div>

                      <Button 
                        variant={breathingActive ? "outline" : "primary"} 
                        className={breathingActive ? "border-teal-600 text-teal-800" : "bg-teal-600 hover:bg-teal-700"}
                        onClick={() => setBreathingActive(!breathingActive)}
                      >
                          {breathingActive ? "Stop Exercise" : "Start Breathing Exercise"}
                      </Button>
                  </Card>
              </div>

              {/* Crisis Numbers */}
              <div className="space-y-4">
                  <Card className="p-6 bg-rose-50 border-rose-200">
                      <h3 className="font-bold text-rose-800 flex items-center gap-2 text-lg">
                          <span>🆘</span> Crisis Support
                      </h3>
                      <p className="text-sm text-rose-700 mt-2 mb-4">If you are feeling suicidal or overwhelmed, please talk to someone now.</p>
                      
                      <div className="space-y-3">
                          <a href="tel:116123" className="block bg-white p-3 rounded-lg border border-rose-200 text-rose-900 font-bold hover:bg-rose-100 transition-colors flex justify-between">
                              <span>Samaritans</span>
                              <span>116 123</span>
                          </a>
                          <a href="https://www.thecalmzone.net/" target="_blank" rel="noreferrer" className="block bg-white p-3 rounded-lg border border-rose-200 text-rose-900 font-bold hover:bg-rose-100 transition-colors flex justify-between">
                              <span>CALM (Men's Support)</span>
                              <span>Webchat →</span>
                          </a>
                          <a href="tel:999" className="block bg-rose-600 text-white p-3 rounded-lg font-bold text-center hover:bg-rose-700 transition-colors">
                              Emergency: 999
                          </a>
                      </div>
                  </Card>
                  
                  <Card className="p-6 bg-indigo-50 border-indigo-200">
                      <h3 className="font-bold text-indigo-900 mb-2">Remember</h3>
                      <p className="text-sm text-indigo-800 italic">"You cannot pour from an empty cup. Taking care of your mental health is part of taking care of your children."</p>
                  </Card>
              </div>
          </div>
      )}

      {activeTab === 'links' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-fade-in">
              <a href="https://www.gov.uk/find-court-tribunal" target="_blank" rel="noreferrer" className="block group">
                  <Card hover className="p-6 flex items-start gap-4">
                      <div className="text-3xl grayscale group-hover:grayscale-0 transition-all">🏛️</div>
                      <div>
                          <h3 className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">Find a Court</h3>
                          <p className="text-sm text-slate-500 mt-1">Official Gov.uk search tool to find the address and contact details of your family court.</p>
                      </div>
                  </Card>
              </a>

              <a href="https://www.gov.uk/government/publications/form-c100-application-under-the-children-act-1989-for-a-child-arrangements-prohibited-steps-specific-issue-section-8-order-or-to-vary-or-discharge" target="_blank" rel="noreferrer" className="block group">
                  <Card hover className="p-6 flex items-start gap-4">
                      <div className="text-3xl grayscale group-hover:grayscale-0 transition-all">📋</div>
                      <div>
                          <h3 className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">Form C100 (Official)</h3>
                          <p className="text-sm text-slate-500 mt-1">Download the official PDF form or apply online via the Gov.uk portal.</p>
                      </div>
                  </Card>
              </a>

              <a href="https://www.cafcass.gov.uk/grown-ups/parents-and-carers/divorce-and-separation/parenting-plan/" target="_blank" rel="noreferrer" className="block group">
                  <Card hover className="p-6 flex items-start gap-4">
                      <div className="text-3xl grayscale group-hover:grayscale-0 transition-all">🛡️</div>
                      <div>
                          <h3 className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">Cafcass Resources</h3>
                          <p className="text-sm text-slate-500 mt-1">Official guidance from Cafcass on parenting plans and what they look for.</p>
                      </div>
                  </Card>
              </a>

              <a href="https://www.judiciary.uk/you-and-the-judiciary/going-to-court/advice-for-litigants-in-person/" target="_blank" rel="noreferrer" className="block group">
                  <Card hover className="p-6 flex items-start gap-4">
                      <div className="text-3xl grayscale group-hover:grayscale-0 transition-all">⚖️</div>
                      <div>
                          <h3 className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">Litigant in Person Guidelines</h3>
                          <p className="text-sm text-slate-500 mt-1">Judiciary.uk official advice for representing yourself in court.</p>
                      </div>
                  </Card>
              </a>
          </div>
      )}

    </Layout>
  );
};

export default Resources;
